import React, { useEffect, useState } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { researchReportService, ResearchReport } from '../services/researchReportService';
import { stockService, StockMonitorTask, StockRecommendation, IntradaySignal } from '../services/stockService';
import PageHeader from '../components/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import ResearchReportModal from '../components/ResearchReports/ResearchReportModal';

import IntradayFilterBar from '../components/ResearchReports/IntradayFilterBar';

const IntradayBacktestModal = ({
  result,
  loading,
  onClose,
}: {
  result: any;
  loading: boolean;
  onClose: () => void;
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-slate-900/90 border border-white/10 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span className="bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded text-sm border border-cyan-500/30">
                {result?.symbol || 'Loading...'}
              </span>
              <span>策略回测: {result?.strategyName || '...'}</span>
            </h2>
            <p className="text-sm text-white/50 mt-1">最近 30 天 (15分钟级别)</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white/70 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : !result ? (
            <div className="text-center text-white/50">暂无数据或回测失败</div>
          ) : (
            <>
              {/* 核心指标 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                  <div className="text-white/50 text-xs mb-1">总收益率</div>
                  <div className={`text-xl font-bold ${result.totalReturn >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {result.totalReturn > 0 ? '+' : ''}
                    {result.totalReturn.toFixed(2)}%
                  </div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                  <div className="text-white/50 text-xs mb-1">胜率</div>
                  <div className="text-xl font-bold text-blue-400">{result.winRate.toFixed(1)}%</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                  <div className="text-white/50 text-xs mb-1">最大回撤</div>
                  <div className="text-xl font-bold text-red-400">{result.maxDrawdown.toFixed(2)}%</div>
                </div>
                <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                  <div className="text-white/50 text-xs mb-1">交易次数</div>
                  <div className="text-xl font-bold text-white">{result.totalTrades}</div>
                </div>
              </div>

              {/* 交易记录 */}
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-white border-l-4 border-purple-500 pl-3">交易记录</h3>
                <div className="overflow-x-auto bg-white/5 rounded-xl border border-white/10">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="text-white/40 text-xs border-b border-white/10 bg-white/5">
                        <th className="p-3">买入时间</th>
                        <th className="p-3 text-right">买入价</th>
                        <th className="p-3">卖出时间</th>
                        <th className="p-3 text-right">卖出价</th>
                        <th className="p-3 text-right">收益率</th>
                        <th className="p-3">原因</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.trades.map((trade: any, idx: number) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors text-sm">
                          <td className="p-3 text-white/60">{trade.entryTime}</td>
                          <td className="p-3 text-right font-mono text-white">{trade.entryPrice.toFixed(2)}</td>
                          <td className="p-3 text-white/60">{trade.exitTime}</td>
                          <td className="p-3 text-right font-mono text-white">{trade.exitPrice.toFixed(2)}</td>
                          <td
                            className={`p-3 text-right font-bold font-mono ${trade.pnlPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}
                          >
                            {trade.pnlPercent > 0 ? '+' : ''}
                            {trade.pnlPercent.toFixed(2)}%
                          </td>
                          <td className="p-3 text-xs text-white/40">{trade.reason}</td>
                        </tr>
                      ))}
                      {result.trades.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-white/30">
                            无交易记录
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ResearchReports: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const [activeTab, setActiveTab] = useState<'daily-picks' | 'reports' | 'history-picks' | 'intraday-signals'>(
    'daily-picks',
  );
  const [reports, setReports] = useState<ResearchReport[]>([]);
  const [dailyPicks, setDailyPicks] = useState<StockMonitorTask[]>([]);
  const [historyPicks, setHistoryPicks] = useState<StockRecommendation[]>([]);
  const [intradaySignals, setIntradaySignals] = useState<IntradaySignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<ResearchReport | null>(null);
  const [selectedPick, setSelectedPick] = useState<StockMonitorTask | null>(null);

  // Backtest state
  const [backtestResult, setBacktestResult] = useState<any | null>(null);
  const [showBacktestModal, setShowBacktestModal] = useState(false);
  const [backtestLoading, setBacktestLoading] = useState(false);

  // Intraday Signal Filters
  const [intradaySymbolFilter, setIntradaySymbolFilter] = useState('');
  const [intradayActionFilter, setIntradayActionFilter] = useState<'all' | 'BUY' | 'SELL'>('all');
  const [intradayStrategyFilter, setIntradayStrategyFilter] = useState('all');

  const handleRunBacktest = async (signal: IntradaySignal) => {
    try {
      setBacktestLoading(true);
      setShowBacktestModal(true);
      setBacktestResult(null);
      const result = await stockService.runIntradayBacktest({
        symbol: signal.symbol,
        strategyId: signal.strategyId,
      });
      setBacktestResult(result);
    } catch (error) {
      console.error(error);
      setBacktestResult(null);
    } finally {
      setBacktestLoading(false);
    }
  };

  // Metadata renderer for Intraday Signal Card
  const renderSignalMetadata = (signal: IntradaySignal) => {
    if (!signal.metadata) return null;
    const { type, ...data } = signal.metadata;

    if (type === 'RSI_OVERSOLD' || type === 'RSI_OVERBOUGHT') {
      return (
        <div className="mt-2 text-xs grid grid-cols-2 gap-1 bg-white/5 p-2 rounded">
          <div className="text-white/40">
            RSI: <span className="text-white">{data.rsi}</span>
          </div>
          <div className="text-white/40">
            Prev: <span className="text-white">{data.prevRsi}</span>
          </div>
        </div>
      );
    }
    if (type === 'BOLL_BREAKOUT' || type === 'BOLL_MIDDLE_BREAKDOWN') {
      return (
        <div className="mt-2 text-xs grid grid-cols-2 gap-1 bg-white/5 p-2 rounded">
          <div className="text-white/40">
            Up: <span className="text-white">{data.upper}</span>
          </div>
          <div className="text-white/40">
            Vol: <span className="text-white">{data.volumeRatio}x</span>
          </div>
        </div>
      );
    }
    if (type === 'MACD_CROSS' || type === 'MACD_DEATH_CROSS') {
      return (
        <div className="mt-2 text-xs grid grid-cols-3 gap-1 bg-white/5 p-2 rounded">
          <div className="text-white/40">
            DIF: <span className="text-white">{data.dif}</span>
          </div>
          <div className="text-white/40">
            DEA: <span className="text-white">{data.dea}</span>
          </div>
          <div className="text-white/40">
            MACD: <span className="text-white">{data.macd}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const filteredIntradaySignals = intradaySignals.filter((signal) => {
    const matchSymbol = intradaySymbolFilter
      ? signal.symbol.toLowerCase().includes(intradaySymbolFilter.toLowerCase())
      : true;
    const matchAction = intradayActionFilter === 'all' ? true : signal.action === intradayActionFilter;
    const matchStrategy = intradayStrategyFilter === 'all' ? true : signal.strategyId === intradayStrategyFilter;
    return matchSymbol && matchAction && matchStrategy;
  });

  const fetchData = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (activeTab === 'daily-picks') {
        const data = await stockService.getDailyPicks();
        setDailyPicks(data || []);
      } else if (activeTab === 'history-picks') {
        const { data } = await stockService.getRecommendationHistory({ limit: 100 });
        setHistoryPicks(data || []);
      } else if (activeTab === 'intraday-signals') {
        const data = await stockService.getIntradaySignals();
        setIntradaySignals(data || []);
      } else {
        const data = await researchReportService.getResearchReports(50, 0);
        setReports(data.alerts || []);
      }
    } catch (err: any) {
      setError(err.message || '获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getReportTitle = (report: ResearchReport) => {
    return report.title || report.template?.feishu?.title || `关于 ${report.symbol} 的调研报告`;
  };

  const getReportContent = (report: ResearchReport) => {
    return report.content || report.template?.feishu?.content?.text || '暂无内容';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 每日精选详情弹窗（回测详情）
  const PickDetailModal = ({ pick, onClose }: { pick: StockMonitorTask; onClose: () => void }) => {
    const results = pick.backtestOptimization?.results || ({} as any);
    const bestParams = pick.backtestOptimization?.bestParams || {};

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-slate-900/90 border border-white/10 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5">
            <div>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <span className="bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded text-sm border border-cyan-500/30">
                  {pick.symbol}
                </span>
                <span>策略详情: {pick.strategyId}</span>
              </h2>
              <p className="text-sm text-white/50 mt-1">
                分析评分: <span className="text-yellow-400 font-bold">{pick.analysisScore}</span>
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-white/70 hover:text-white">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-6">
            {/* 核心指标 */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                <div className="text-white/50 text-xs mb-1">{results.annualReturn ? '年化收益率' : '总收益率'}</div>
                <div
                  className={`text-xl font-bold ${Number(results.annualReturn || results.totalReturn || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                >
                  {results.annualReturn
                    ? `${(Number(results.annualReturn) * 100).toFixed(2)}%`
                    : results.totalReturn
                      ? `${(Number(results.totalReturn) * 100).toFixed(2)}%`
                      : '-'}
                </div>
              </div>
              <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                <div className="text-white/50 text-xs mb-1">夏普比率</div>
                <div className="text-xl font-bold text-white">
                  {results.sharpeRatio ? Number(results.sharpeRatio).toFixed(2) : '-'}
                </div>
              </div>
              <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                <div className="text-white/50 text-xs mb-1">最大回撤</div>
                <div className="text-xl font-bold text-red-400">
                  {results.maxDrawdown ? `${(Number(results.maxDrawdown) * 100).toFixed(2)}%` : '-'}
                </div>
              </div>
              <div className="bg-white/5 p-4 rounded-lg border border-white/5">
                <div className="text-white/50 text-xs mb-1">胜率</div>
                <div className="text-xl font-bold text-blue-400">
                  {results.winRate ? `${(Number(results.winRate) * 100).toFixed(2)}%` : '-'}
                </div>
              </div>
            </div>

            {/* 详细参数 */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white border-l-4 border-cyan-500 pl-3">最优参数配置 (WFO)</h3>
              <div className="bg-black/20 p-4 rounded-lg border border-white/5 font-mono text-sm text-cyan-300">
                {JSON.stringify(bestParams, null, 2)}
              </div>
            </div>

            {/* 交易统计 */}
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white border-l-4 border-purple-500 pl-3">交易统计</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex justify-between p-3 bg-white/5 rounded">
                  <span className="text-white/60">总交易次数</span>
                  <span className="text-white font-bold">{results.totalTrades || '-'}</span>
                </div>
                <div className="flex justify-between p-3 bg-white/5 rounded">
                  <span className="text-white/60">盈利因子</span>
                  <span className="text-white font-bold">
                    {results.profitFactor ? parseFloat(results.profitFactor).toFixed(2) : '-'}
                  </span>
                </div>
              </div>
            </div>

            <div className="text-xs text-white/30 pt-4 border-t border-white/5">
              上次优化时间: {formatDate(pick.backtestOptimization?.lastRunTime || '')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="relative z-10 min-h-screen flex flex-col pb-20">
        <PageHeader title="AI 深度研报" onBack={() => setCurrentPage('home')} />

        {/* Tabs */}
        <div className="px-4 mb-4">
          <div className="flex bg-white/5 p-1 rounded-lg backdrop-blur-sm border border-white/10">
            <button
              onClick={() => setActiveTab('daily-picks')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'daily-picks'
                  ? 'bg-cyan-500/20 text-cyan-300 shadow-lg border border-cyan-500/30'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              每日精选
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'reports'
                  ? 'bg-purple-500/20 text-purple-300 shadow-lg border border-purple-500/30'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              深度研报
            </button>
            <button
              onClick={() => setActiveTab('history-picks')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'history-picks'
                  ? 'bg-green-500/20 text-green-300 shadow-lg border border-green-500/30'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              历史回顾
            </button>
            <button
              onClick={() => setActiveTab('intraday-signals')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all duration-300 ${
                activeTab === 'intraday-signals'
                  ? 'bg-blue-500/20 text-blue-300 shadow-lg border border-blue-500/30'
                  : 'text-white/50 hover:text-white hover:bg-white/5'
              }`}
            >
              盘中信号
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center text-red-400 p-8 bg-white/5 rounded-xl border border-red-500/30">
              <p>{error}</p>
              <button
                onClick={fetchData}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
              >
                重试
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'daily-picks' &&
                (dailyPicks.length === 0 ? (
                  <div className="text-center text-white/50 p-12">
                    <div className="text-4xl mb-4">💎</div>
                    <p>今日暂无精选个股</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {dailyPicks.map((pick, idx) => (
                      <div
                        key={`${pick.symbol}-${idx}`}
                        onClick={() => setSelectedPick(pick)}
                        className="cursor-pointer group"
                      >
                        <GlassCard className="h-full hover:bg-white/10 transition-all duration-300 border border-white/5 hover:border-cyan-500/30 group-hover:transform group-hover:scale-[1.02]">
                          <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-white">{pick.symbol}</span>
                              <span className="text-xs bg-white/10 px-2 py-0.5 rounded text-white/60">
                                {pick.strategyId}
                              </span>
                            </div>
                            <div className="text-yellow-400 font-bold text-sm bg-yellow-400/10 px-2 py-1 rounded border border-yellow-400/20">
                              {pick.analysisScore}分
                            </div>
                          </div>

                          {/* Key Metrics Preview */}
                          <div className="grid grid-cols-3 gap-2 mb-4">
                            <div className="bg-white/5 p-2 rounded">
                              <div className="text-white/40 text-[10px]">年化收益</div>
                              <div
                                className={`font-mono text-sm ${Number(pick.backtestOptimization?.results?.annualReturn || pick.backtestOptimization?.results?.totalReturn || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                              >
                                {pick.backtestOptimization?.results?.annualReturn
                                  ? `${(Number(pick.backtestOptimization.results.annualReturn) * 100).toFixed(0)}%`
                                  : pick.backtestOptimization?.results?.totalReturn
                                    ? `${(Number(pick.backtestOptimization.results.totalReturn) * 100).toFixed(0)}%`
                                    : '-'}
                              </div>
                            </div>
                            <div className="bg-white/5 p-2 rounded">
                              <div className="text-white/40 text-[10px]">夏普比率</div>
                              <div className="text-white font-mono text-sm">
                                {pick.backtestOptimization?.results?.sharpeRatio
                                  ? Number(pick.backtestOptimization.results.sharpeRatio).toFixed(2)
                                  : '-'}
                              </div>
                            </div>
                            <div className="bg-white/5 p-2 rounded">
                              <div className="text-white/40 text-[10px]">胜率</div>
                              <div className="text-blue-400 font-mono text-sm">
                                {pick.backtestOptimization?.results?.winRate
                                  ? `${(Number(pick.backtestOptimization.results.winRate) * 100).toFixed(0)}%`
                                  : '-'}
                              </div>
                            </div>
                          </div>

                          <div className="text-white/50 text-xs line-clamp-2 mb-3">
                            {pick.currentData
                              ? `最新价: ${pick.currentData.close || '-'} | 成交量: ${pick.currentData.volume || '-'}`
                              : pick.marketData
                                ? `最新价: ${pick.marketData.close || '-'} | 成交量: ${pick.marketData.volume || '-'}`
                                : '暂无行情数据'}
                          </div>

                          <div className="flex justify-end pt-2 border-t border-white/5">
                            <span className="text-cyan-400 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              查看回测详情
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </GlassCard>
                      </div>
                    ))}
                  </div>
                ))}

              {activeTab === 'history-picks' &&
                (historyPicks.length === 0 ? (
                  <div className="text-center text-white/50 p-12">
                    <div className="text-4xl mb-4">🕰️</div>
                    <p>暂无历史推荐记录</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Performance Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white/5 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
                        <div className="text-white/50 text-xs mb-1">总推荐数</div>
                        <div className="text-2xl font-bold text-white">{historyPicks.length}</div>
                        <div className="text-xs text-white/40 mt-1">
                          已验证: {historyPicks.filter((p) => p.status === 'verified').length}
                        </div>
                      </div>
                      <div className="bg-white/5 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
                        <div className="text-white/50 text-xs mb-1">T+1 胜率</div>
                        <div className="text-2xl font-bold text-red-400">
                          {(() => {
                            const verified = historyPicks.filter((p) => p.status === 'verified');
                            if (verified.length === 0) return '-';
                            // 胜率计算逻辑：如果 AlphaT1 > 0 则视为跑赢大盘（胜），否则败
                            // 或者也可以使用 returnT1 > 0 作为绝对收益胜率
                            // 这里我们展示 "Alpha 胜率" (跑赢大盘的概率)
                            const wins = verified.filter((p) => Number(p.alphaT1 || 0) > 0).length;
                            return `${((wins / verified.length) * 100).toFixed(1)}%`;
                          })()}
                        </div>
                        <div className="text-xs text-white/40 mt-1">Alpha 胜率 (T+1)</div>
                      </div>
                      <div className="bg-white/5 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
                        <div className="text-white/50 text-xs mb-1">平均 Alpha (T+1)</div>
                        <div
                          className={`text-2xl font-bold ${(() => {
                            const verified = historyPicks.filter((p) => p.status === 'verified');
                            if (verified.length === 0) return 'text-white';
                            const avg = verified.reduce((sum, p) => sum + Number(p.alphaT1 || 0), 0) / verified.length;
                            return avg >= 0 ? 'text-green-400' : 'text-red-400';
                          })()}`}
                        >
                          {(() => {
                            const verified = historyPicks.filter((p) => p.status === 'verified');
                            if (verified.length === 0) return '-';
                            const avg = verified.reduce((sum, p) => sum + Number(p.alphaT1 || 0), 0) / verified.length;
                            return `${avg > 0 ? '+' : ''}${(avg * 100).toFixed(2)}%`;
                          })()}
                        </div>
                        <div className="text-xs text-white/40 mt-1">平均超额收益</div>
                      </div>
                      <div className="bg-white/5 p-4 rounded-xl border border-white/10 backdrop-blur-sm">
                        <div className="text-white/50 text-xs mb-1">平均绝对收益 (T+1)</div>
                        <div
                          className={`text-2xl font-bold ${(() => {
                            const verified = historyPicks.filter((p) => p.status === 'verified');
                            if (verified.length === 0) return 'text-white';
                            const avg = verified.reduce((sum, p) => sum + Number(p.returnT1 || 0), 0) / verified.length;
                            return avg >= 0 ? 'text-green-400' : 'text-red-400';
                          })()}`}
                        >
                          {(() => {
                            const verified = historyPicks.filter((p) => p.status === 'verified');
                            if (verified.length === 0) return '-';
                            const avg = verified.reduce((sum, p) => sum + Number(p.returnT1 || 0), 0) / verified.length;
                            return `${avg > 0 ? '+' : ''}${(avg * 100).toFixed(2)}%`;
                          })()}
                        </div>
                        <div className="text-xs text-white/40 mt-1">单次平均涨跌幅</div>
                      </div>
                    </div>

                    <div className="overflow-x-auto bg-white/5 rounded-xl border border-white/10">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="text-white/40 text-xs border-b border-white/10 bg-white/5">
                            <th className="p-4">日期</th>
                            <th className="p-4">股票</th>
                            <th className="p-4">策略</th>
                            <th className="p-4 text-right">推荐价格</th>
                            <th className="p-4 text-right">T+1表现</th>
                            <th className="p-4 text-right">T+3表现</th>
                            <th className="p-4 text-right">T+5表现</th>
                            <th className="p-4 text-center">状态</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyPicks.map((pick) => (
                            <tr
                              key={pick.id}
                              className="border-b border-white/5 hover:bg-white/5 transition-colors text-sm"
                            >
                              <td className="p-4 text-white/60">{pick.date}</td>
                              <td className="p-4">
                                <div className="font-bold text-white">{pick.symbol}</div>
                                <div className="text-xs text-white/40">{pick.name || '-'}</div>
                              </td>
                              <td className="p-4 text-white/60">
                                <span className="bg-white/10 px-2 py-0.5 rounded text-xs">{pick.strategyId}</span>
                              </td>
                              <td className="p-4 text-right font-mono text-white">{pick.price.toFixed(2)}</td>
                              <td className="p-4 text-right">
                                <div
                                  className={`font-mono ${(pick.returnT1 || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                                >
                                  {pick.returnT1 != null ? `${(pick.returnT1 * 100).toFixed(2)}%` : '-'}
                                </div>
                                {pick.alphaT1 != null && (
                                  <div
                                    className={`text-xs font-mono ${(pick.alphaT1 || 0) >= 0 ? 'text-green-400/70' : 'text-red-400/70'}`}
                                  >
                                    α: {(pick.alphaT1 || 0) > 0 ? '+' : ''}
                                    {((pick.alphaT1 || 0) * 100).toFixed(2)}%
                                  </div>
                                )}
                              </td>
                              <td className="p-4 text-right">
                                <div
                                  className={`font-mono ${(pick.returnT3 || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                                >
                                  {pick.returnT3 != null ? `${(pick.returnT3 * 100).toFixed(2)}%` : '-'}
                                </div>
                                {pick.alphaT3 != null && (
                                  <div
                                    className={`text-xs font-mono ${(pick.alphaT3 || 0) >= 0 ? 'text-green-400/70' : 'text-red-400/70'}`}
                                  >
                                    α: {(pick.alphaT3 || 0) > 0 ? '+' : ''}
                                    {((pick.alphaT3 || 0) * 100).toFixed(2)}%
                                  </div>
                                )}
                              </td>
                              <td className="p-4 text-right">
                                <div
                                  className={`font-mono ${(pick.returnT5 || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                                >
                                  {pick.returnT5 != null ? `${(pick.returnT5 * 100).toFixed(2)}%` : '-'}
                                </div>
                                {pick.alphaT5 != null && (
                                  <div
                                    className={`text-xs font-mono ${(pick.alphaT5 || 0) >= 0 ? 'text-green-400/70' : 'text-red-400/70'}`}
                                  >
                                    α: {(pick.alphaT5 || 0) > 0 ? '+' : ''}
                                    {((pick.alphaT5 || 0) * 100).toFixed(2)}%
                                  </div>
                                )}
                              </td>
                              <td className="p-4 text-center">
                                <span
                                  className={`px-2 py-0.5 rounded text-xs border ${
                                    pick.status === 'verified'
                                      ? 'bg-green-500/10 text-green-300 border-green-500/20'
                                      : pick.status === 'failed'
                                        ? 'bg-red-500/10 text-red-300 border-red-500/20'
                                        : 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20'
                                  }`}
                                >
                                  {pick.status === 'verified' ? '已验证' : pick.status === 'failed' ? '失败' : '待验证'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

              {activeTab === 'reports' &&
                (reports.length === 0 ? (
                  <div className="text-center text-white/50 p-12">
                    <div className="text-4xl mb-4">📝</div>
                    <p>暂无研报数据</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reports.map((report) => (
                      <div key={report.id} onClick={() => setSelectedReport(report)} className="cursor-pointer group">
                        <GlassCard className="h-full hover:bg-white/10 transition-all duration-300 border border-white/5 hover:border-purple-500/30 group-hover:transform group-hover:scale-[1.02]">
                          <div className="flex justify-between items-start mb-3">
                            <div className="bg-purple-500/20 text-purple-300 text-xs px-2 py-1 rounded border border-purple-500/30">
                              {report.symbol}
                            </div>
                            <span className="text-white/40 text-xs">{formatDate(report.timestamp)}</span>
                          </div>

                          <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 group-hover:text-purple-400 transition-colors">
                            {getReportTitle(report)}
                          </h3>

                          <p className="text-white/60 text-sm line-clamp-3 mb-4">
                            {getReportContent(report).replace(/[#*`]/g, '').slice(0, 100)}...
                          </p>

                          <div className="flex justify-end">
                            <span className="text-purple-400 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              阅读全文
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                        </GlassCard>
                      </div>
                    ))}
                  </div>
                ))}

              {activeTab === 'intraday-signals' && (
                <div className="space-y-4">
                  <IntradayFilterBar
                    symbolFilter={intradaySymbolFilter}
                    onSymbolFilterChange={setIntradaySymbolFilter}
                    onSymbolSearch={() => {}}
                    selectedAction={intradayActionFilter}
                    onActionChange={setIntradayActionFilter}
                    selectedStrategy={intradayStrategyFilter}
                    onStrategyChange={setIntradayStrategyFilter}
                  />

                  {filteredIntradaySignals.length === 0 ? (
                    <div className="text-center text-white/50 p-12">
                      <div className="text-4xl mb-4">📡</div>
                      <p>暂无盘中信号</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {filteredIntradaySignals.map((signal) => (
                        <div key={signal.id} className="group">
                          <GlassCard className="h-full hover:bg-white/10 transition-all duration-300 border border-white/5 hover:border-blue-500/30">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-lg font-bold text-white">{signal.symbol}</span>
                                <span
                                  className={`px-2 py-0.5 rounded text-xs font-bold border ${
                                    signal.action === 'BUY'
                                      ? 'bg-green-500/20 text-green-300 border-green-500/30'
                                      : 'bg-red-500/20 text-red-300 border-red-500/30'
                                  }`}
                                >
                                  {signal.action === 'BUY' ? '买入' : '卖出'}
                                </span>
                              </div>
                              <span className="text-white/40 text-xs font-mono">{formatDate(signal.time)}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 mb-3">
                              <div className="bg-white/5 p-2 rounded">
                                <div className="text-white/40 text-[10px]">触发价格</div>
                                <div className="text-white font-mono text-sm">{signal.price}</div>
                              </div>
                              <div className="bg-white/5 p-2 rounded">
                                <div className="text-white/40 text-[10px]">策略评分</div>
                                <div className="text-yellow-400 font-mono text-sm">{signal.score || '-'}</div>
                              </div>
                            </div>

                            <div className="text-white/60 text-xs line-clamp-2 mb-2">
                              <span className="text-blue-400 mr-1">[{signal.strategyId}]</span>
                              {signal.reason || '无详细说明'}
                            </div>

                            {/* Render detailed metadata if available */}
                            {renderSignalMetadata(signal)}

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRunBacktest(signal);
                              }}
                              className="w-full mt-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center justify-center gap-1"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                />
                              </svg>
                              运行回测 (30天)
                            </button>
                          </GlassCard>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {selectedReport && <ResearchReportModal report={selectedReport} onClose={() => setSelectedReport(null)} />}

        {selectedPick && <PickDetailModal pick={selectedPick} onClose={() => setSelectedPick(null)} />}

        {showBacktestModal && (
          <IntradayBacktestModal
            result={backtestResult}
            loading={backtestLoading}
            onClose={() => setShowBacktestModal(false)}
          />
        )}
      </div>
    </GradientBackground>
  );
};

export default ResearchReports;
